import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import {
  ref,
  nextTick,
  watch,
  provide,
  onMounted,
  inject,
  onBeforeUnmount,
} from "vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import LineInfoDetailHeader from "@/components/line/LineInfoDetailHeader.vue";
import LineInfoDetailContent from "@/components/line/LineInfoDetailContent.vue";
import LineInfoDetailSelectBusStop from "@/components/line/LineInfoDetailSelectBusStop.vue";
import LineInfoDetailFooter from "@/components/line/LineInfoDetailFooter.vue";
import BookDetail from "@/views/book/BookDetail.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import InfoModal from "@/components/shared/modal/base/InfoModal.vue";
import Page from "@/components/shared/Page.vue";
import SelectModal from "@/components/shared/modal/base/SelectModal.vue";
import router from "@/router";
import { IBusStop, IBusStopData } from "@/domain/entities/busStop";
import { useLineStore } from "@/stores/lineStore";
import { useRoute } from "vue-router";
import {
  FavoriteLine,
  IFavoriteLine,
  IFavoriteLineData,
} from "@/domain/entities/favoriteLine";
import {
  IArrivalTimeOption,
  IBus,
  IBusLocation,
  ILineFare,
  ILineInfo,
} from "@/domain/entities/lineInfo";
import { computed } from "@vue/reactivity";
import {
  ACTION_MESSAGES,
  ArrivalTimeOptionCode,
  arrivalTimeOptions,
  BookIntervalCode,
  BUS_LOCATION_REFRESH_TIME_MILLIS,
  LineTypeCode,
  MessageTypeCode,
  MessageTypeCodeType,
  PROVIDE_KEY_BUS_LOCATIONS,
  PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
} from "@/types";
import deviceHelper from "@/utils/deviceInfo";

import LineTurnPathMap from "@/components/map/LineTurnPathMap.vue";
import BusStopLayoutModal from "@/components/line/BusStopLayoutModal.vue";
import TimeTableModal from "@/components/line/TimeTableModal.vue";
import BusInfoModal from "@/components/shared/modal/BusInfoModal.vue";
import MonthlyBookSeatAndPayment from "@/components/book/MonthlyBookSeatAndPayment.vue";
import { IBookableDatesInfo } from "@/domain/entities/book";
import MonthlyBookDetailWithDuplicatedSeat from "../book/MonthlyBookDetailWithDuplicatedSeat.vue";
import PastArrivalTimeModal from "@/components/line/PastArrivalTimeModal.vue";
import dayjs from "dayjs";
import { useArrivalTimesHandler } from "@/composables/line/useArrivalTimesHandler";
import { useServiceStore } from "@/stores/serviceStore";
import { useTurnBusLastSeatCountList } from "@/composables/line/useTurnBusLastSeatCountList";
import BusOrderSelectModal from "@/components/line/BusOrderSelectModal.vue";
import { setIntervalHandler } from "@/utils/setIntervalHandler";
import LineInfoDetailExtended from "@/components/line/LineInfoDetailExtended.vue";
import FavoriteBusStopTypeSelectModal from "@/components/shared/modal/FavoriteBusStopTypeSelectModal.vue";

import { useBusLocationDrivingStateManager } from "@/composables/line/useBusLocationDrivingStateManager";
import { useBusLocationFetchConfig } from "@/composables/line/useBusLocationFetchConfig";
import { useShouldGetBusLocation } from "@/composables/map/useShouldGetBusLocation";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";
import SegmentedControl from "@/components/shared/SegmentedControl.vue";
import getBusStopsByDate from "@/use/useGetBusStopsByDate";
import getPathsByDate from "@/use/useGetPathsByDate";
import useGetEarlyDriveWeekdays from "@/use/useGetEarlyDriveWeekdays";
import { useDriveDatesItems } from "@/composables/line/useDriveDatesItems";

interface Segment {
  name: string;
  dateTime: string;
}


const __default__ = {
  name: "LineInfoDetail",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

_useCssVars(_ctx => ({
  "91c807b8-segmentHeightPx": (_unref(segmentHeightPx))
}))

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const serviceStore = useServiceStore();
const lineStore = useLineStore();
const route = useRoute();

/**
 * 정류장 변경(추가/폐지) 일시 세그먼트(최대 2개)
 * 예약노선은 기/종점 정류장을 변경 할 수 없음(관리자단 백엔드에서 막고 있음)
 */
const segmentedControl = ref<InstanceType<typeof SegmentedControl>>();

const segments = computed<Segment[]>(() => {
  const groups = selectedTurn.value?.busStopGroupsByPeriod ?? [];
  const maxVisibleGroups = 2;

  return groups.slice(0, maxVisibleGroups).map((group) => {
    const beginDate = dayjs(group.useBeginDtm);
    const today = dayjs().format("YYYY-MM-DD HH:mm");
    const willChange = beginDate.isAfter(dayjs(), "minute");

    const name = willChange
      ? beginDate.format("HH:mm") === "00:00"
        ? `${beginDate.format("M/D")} 이후`
        : `${beginDate.format("M/D HH:mm")} 이후`
      : "현재";
    const dateTime = willChange ? group.useBeginDtm : today;

    return { name, dateTime };
  });
});

const shouldShowSegment = computed(() => segments.value.length > 1);

const segmentHeightPx = computed(() =>
  shouldShowSegment.value ? "38px" : "0px"
);

const segmentTitles = computed(() =>
  segments.value.map((segment) => segment.name)
);

const currentSegmentIndex = ref(0);

const onChangedSegmentIndex = (index: number) => {
  currentSegmentIndex.value = index;
  // 승/하차 정류장 선택 상태를 초기화한다.
  resetSelectedBusStopIfNeeded();
  // checkFavoriteLineBySegmentChange();
};

const selectedSegmentDateTime = computed(
  (): string | undefined => segments.value[currentSegmentIndex.value]?.dateTime
);

/** 현재 탭이 선택된 상태인지 여부 */
const isActiveCurrentSegment = computed(() => {
  if (!selectedSegmentDateTime.value) return true;

  return dayjs().isSameOrAfter(dayjs(selectedSegmentDateTime.value), "minute");
});

/** 선택된 회차 및 일자의 경로 */
const selectedTurnPaths = computed(() => {
  if (!selectedTurn.value?.pathGroupsByPeriod?.length) {
    return selectedTurn.value?.paths ?? [];
  }

  const today = dayjs().format("YYYY-MM-DD HH:mm");
  const selectedBeginDtm = selectedSegmentDateTime.value ?? today;

  return getPathsByDate(selectedTurn.value, selectedBeginDtm);
});

/** 선택된 회차 및 일자의 정류장 목록 */
const selectedTurnBusStops = computed(() => {
  if (!selectedTurn.value?.busStopGroupsByPeriod?.length) {
    return selectedTurn.value?.busStops ?? [];
  }

  const today = dayjs().format("YYYY-MM-DD HH:mm");
  const selectedBeginDtm = selectedSegmentDateTime.value ?? today;

  return getBusStopsByDate(selectedTurn.value, selectedBeginDtm);
});

/** 가장 가까운 미래의 정류장 변경 일시 */
const nextBusStopChangeDateTime = computed(() => {
  if (!shouldShowSegment.value) return;

  return segments.value[segments.value.length - 1]?.dateTime;
});
provide(PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME, nextBusStopChangeDateTime);

/** UI 바인딩을 위한 노선 정보 */
const lineId = route.params.lineId.toString();
const lineInfo = ref<ILineInfo>();
const footer = ref<typeof LineInfoDetailFooter>();
const selectBusStopRef =
  ref<InstanceType<typeof LineInfoDetailSelectBusStop>>();

const { shouldGetBusLocationAndFindCampus } = useShouldGetBusLocation();
const shouldShowLocationRefreshButton = ref(true);

/** 실시간 버스 위치 화면 표시 여부 */
const shouldShowBusLocations = computed(
  () => shouldShowLocationRefreshButton.value && isActiveCurrentSegment.value
);

/** 버스 도착 시간 선택 화면 표시 여부 */
const shouldShowBusArrivalTimeSelect = computed(
  () => !isInsideShuttle.value && isActiveCurrentSegment.value
);

const driveDatesItemsByTurn = computed(() =>
  useGetEarlyDriveWeekdays(lineInfo.value?.turns ?? []).map(
    (earlyDriveInfos) => {
      return useDriveDatesItems(computed(() => [earlyDriveInfos]))
        .driveDatesItems;
    }
  )
);

const availableTimesForModal = computed(() => {
  const today = dayjs().format("YYYY-MM-DD HH:mm");
  const selectedBeginDtm = selectedSegmentDateTime.value ?? today;
  if (!lineInfo.value?.turns.length) {
    return [];
  }

  return lineInfo.value.turns.map((turn, idx) => {
    const busStops = getBusStopsByDate(turn, selectedBeginDtm);
    const firstBusStop = busStops[0];

    const { name, turnNum, earlyDriveInfos } = turn;
    const turnDisplayText =
      name === turnNum?.toString() ? `${turnNum}` : `${turnNum}(${name})`;

    const timeDispalyText = `${firstBusStop.departureTime} (${turnDisplayText})`;

    if (!earlyDriveInfos?.length) {
      return timeDispalyText;
    }

    // driveDatesItemsByTurn에서 해당 turn의 요일 정보를 가져옴
    const driveDatesItems = driveDatesItemsByTurn.value[idx]?.value ?? [];
    const driveDatesText = driveDatesItems
      .map((date) => date.days.map((day) => day.text).join(", "))
      .join(", ");

    return `${timeDispalyText} / ${driveDatesText}`;
  });
});

const availableTimes = computed(() => {
  const today = dayjs().format("YYYY-MM-DD HH:mm");
  const selectedBeginDtm = selectedSegmentDateTime.value ?? today;

  return (
    lineInfo.value?.turns.map((turn) => {
      const busStops = getBusStopsByDate(turn, selectedBeginDtm);
      const firstBusStop = busStops[0];

      return `${firstBusStop.departureTime}`;
    }) ?? []
  );
});

const selectedTimeIndex = ref(0);
const selectedTurn = computed(
  () => lineInfo?.value?.turns[selectedTimeIndex.value]
);

/**
 * 사내 - 현재 노선 전체버스
 * 그외 - 현재 회차 전체버스
 */
const allBusesMap = computed(() =>
  isInsideShuttle.value
    ? createInsideShuttleBusesMap()
    : createSelectedTurnBusesMap()
);
const createInsideShuttleBusesMap = () => {
  if (!isInsideShuttle.value) return new Map();

  const busMap: Map<string, IBus> = new Map();
  lineInfo.value?.turns.forEach((turn) => {
    if (turn.buses) {
      turn.buses.forEach((bus) => {
        busMap.set(bus.allocUid, bus);
      });
    }
  });

  return busMap;
};

const createSelectedTurnBusesMap = () => {
  if (isInsideShuttle.value) return new Map();

  const busMap: Map<string, IBus> = new Map();
  selectedTurn.value?.buses?.forEach((bus) => {
    busMap.set(bus.allocUid, bus);
  });

  return busMap;
};

const busesForInfoModal = ref<IBus[]>([]);
const updateBusesForInfoModal = (buses: IBus[]) => {
  busesForInfoModal.value = buses;
};

const handleClickBusIcon = (busesImageExisted: IBus[]) => {
  updateBusesForInfoModal(busesImageExisted);

  if (busesForInfoModal.value.length) {
    toggleShowBusInfoModal(true);
  }
};

const isInsideShuttle = computed(
  () => lineInfo.value?.lineTypeCode === LineTypeCode.Inside
);

const isBackwardLine = computed(
  () => lineInfo.value?.lineTypeCode === LineTypeCode.Backward
);

const isBookable = computed(() => !!selectedTurn.value?.bookIntervals?.length);

const dailyBookInterval = computed(() =>
  selectedTurn.value?.bookIntervals?.find(
    (bookInterval) => bookInterval.bookIntervalCode === BookIntervalCode.DAY
  )
);

const monthlyBookInterval = computed(() =>
  selectedTurn.value?.bookIntervals?.find(
    (bookInterval) => bookInterval.bookIntervalCode === BookIntervalCode.MONTH
  )
);

const monthlyBookable = computed(
  () =>
    !!monthlyBookInterval && (selectedTurn.value?.lineFare.monthlyFare ?? 0) > 0
);

const currentFavoriteLine = ref<IFavoriteLine>();

const selectedGetOnBusStop = ref<IBusStop | undefined>(undefined);
const selectedGetOffBusStop = ref<IBusStop | undefined>(undefined);

const updateFavoriteGetOnBusStop = (busStop?: IBusStop) => {
  selectedGetOnBusStop.value = busStop;
};

const updateFavoriteGetOffBusStop = (busStop?: IBusStop) => {
  selectedGetOffBusStop.value = busStop;
};

selectedGetOnBusStop.value = lineStore.getBusStopById(
  route.query.getOnBusStopId?.toString() ?? ""
);
selectedGetOffBusStop.value = lineStore.getBusStopById(
  route.query.getOffBusStopId?.toString() ?? ""
);

const resetSelectedBusStopIfNeeded = () => {
  if (!isBookable.value || isInsideShuttle.value) return;

  selectBusStopRef.value?.resetSelectedBusStops();
  selectedGetOnBusStop.value = undefined;
  selectedGetOffBusStop.value = undefined;
};

const isFavoriteLine = computed(
  () =>
    !!currentFavoriteLine.value &&
    !currentFavoriteLine.value.isTempOnboardFavorite
);
const showFooter = ref(false);

const showSelectTimeModal = ref(false);
const toggleSelectTimeModal = (show: boolean) => {
  showSelectTimeModal.value = show;
};
const onTimeSelected = async (index: number) => {
  setTimeout(() => {
    showSelectTimeModal.value = false;
  }, 300);

  if (selectedTimeIndex.value === index) return;

  selectedTimeIndex.value = index;
  resetHasAllBusesArrivedLastStop();
  resetHasAllBusesEndedDriving();
  updateBusLocationUpdatedAt();
  resetSelectedBusStopIfNeeded();
  await updateBusLocation();
  updateSelectedBus(busLocations.value[0]);
  await handleFetchArrivalTimes(selectedArrivalTimeOption.value);
};

/** 버스 정보 모달 처리 */
const shouldShowBusInfoModal = ref(false);
const toggleShowBusInfoModal = (show: boolean) => {
  shouldShowBusInfoModal.value = show;
};

const checkFavoriteLine = () => {
  let favoriteLine;

  if (isBookable.value) {
    favoriteLine = lineStore.getFavoriteLine(
      selectedTurn.value?.id ?? "",
      selectedGetOnBusStop.value?.id,
      selectedGetOffBusStop.value?.id
    );
  } else {
    favoriteLine = lineStore.getNonBookableFavoriteLine(
      selectedTurn.value?.id ?? ""
    );
  }

  currentFavoriteLine.value = favoriteLine;

  if (!favoriteLine) {
    showFavoriteLineTip();
  }
};

const showFavoriteLineTip = () => {
  footer.value?.showTips(5000);
};

watch([selectedTimeIndex, showFooter], ([selectedTimeIndex, showFooter]) => {
  if (isBookable.value && !showFooter) return;
  checkFavoriteLine();
});

/** 모든 호차에 대한 위치정보 가져오기 */
const busLocations = ref<IBusLocation[]>([]);
provide(PROVIDE_KEY_BUS_LOCATIONS, busLocations);

/** 버스 위치 정보 API의 응답값을 보고, 버스의 주행 상태 파악 */
const {
  updateHasAllBusesArrivedLastStop,
  resetHasAllBusesArrivedLastStop,

  updateHasAllBusesEndedDriving,
  resetHasAllBusesEndedDriving,

  isBusRunning,
  updateBusRunningStatus,

  shouldGetBusLocation,
} = useBusLocationDrivingStateManager();

const {
  isLoggedIn,
  isLocationCacheExpired,
  lastBusStopArrivalThresholdMin,
  updateBusLocationUpdatedAt,
} = useBusLocationFetchConfig();

/** 실시간 버스 위치 API Polling */
const busLocationUpdateIntervalMillis = computed(
  () =>
    serviceStore.serviceInfo?.busLocationUpdateIntervalMillis ||
    BUS_LOCATION_REFRESH_TIME_MILLIS
);

const { setIntervalCallback, clearIntervalCallback } = setIntervalHandler(
  busLocationUpdateIntervalMillis.value
);

const getLastBusStop = () => {
  return isInsideShuttle.value
    ? lineInfo.value?.turns[lineInfo.value.turns.length - 1].lastStop
    : selectedTurn.value?.lastStop;
};

const getFirstBusStop = () => {
  // 기점 정류장
  return isInsideShuttle.value
    ? lineInfo.value?.turns[0].firstStop
    : selectedTurn.value?.firstStop;
};

// 경로보기 화면에서 새로고침 버튼을 클릭한 경우, 버스 위치 확인불가시 토스트메시지 표출
const showToastIfNoBusLocationsOnPathMap = () => {
  if (shouldShowLineTurnPathMap.value && busLocations.value.length === 0) {
    showToastMessage("현재 운행중인 버스 정보를 찾을 수 없습니다");
  }
};

/** 실시간 버스 위치 API 호출 */
const updateBusLocation = async () => {
  if (!lineInfo.value) return;

  const result = await shouldGetBusLocation(
    isLoggedIn.value,
    lastBusStopArrivalThresholdMin.value,
    lineInfo.value.lineTypeCode,
    lineInfo.value.campusName,
    getFirstBusStop(),
    getLastBusStop()
  );

  if (!result.shouldGetLocation) {
    busLocations.value = [];
    isBusRunning.value = false;
    return;
  }

  if (!isLocationCacheExpired()) {
    return;
  }

  const busLocationResult = await lineStore.getBusLocations(
    lineInfo.value?.id ?? "",
    isInsideShuttle.value ? undefined : selectedTurn.value?.id ?? ""
  );

  updateBusLocationUpdatedAt(dayjs());
  busLocations.value = busLocationResult.data ?? [];
  updateHasAllBusesArrivedLastStop(busLocations.value);
  updateHasAllBusesEndedDriving(busLocations.value);
  updateBusRunningStatus(busLocations.value, result.shouldGetLocation);
  if (!selectedBus.value) {
    updateSelectedBus(busLocations.value[0]);
  }
};

const handleRefreshBusLocation = async () => {
  await updateBusLocation();
  showToastIfNoBusLocationsOnPathMap();
  await handleFetchArrivalTimes(selectedArrivalTimeOption.value);
};

/** 선택된 회차의 모든 호차들의 잔여좌석 수 */
const { turnBusLastSeatCountList } = useTurnBusLastSeatCountList(busLocations);

/** 노선 경로 지도 보기 */
const shouldShowLineTurnPathMap = ref(false);
const toggleShowLineTurnPathModal = (show: boolean) => {
  shouldShowLineTurnPathMap.value = show;
};

/** Footer가 보이는 여부에 따라 스크롤 조정 */
const footerHeight = ref("0px");

const contentArea = ref<HTMLElement>();
const bottomNavbarHeight = parseInt(
  getComputedStyle(document.documentElement).getPropertyValue(
    "--bottom-navbar-height"
  )
);
const onChangedBusStopSelection = (
  selectComplete: boolean,
  clickEventOffsetY: number,
  getOnBusStop: IBusStop | undefined,
  getOffBusStop: IBusStop | undefined
) => {
  showFooter.value = selectComplete;
  selectedGetOnBusStop.value = getOnBusStop;
  selectedGetOffBusStop.value = getOffBusStop;

  const bottomPadding =
    deviceHelper.isiOS() && bottomNavbarHeight > 0 ? 20 : bottomNavbarHeight;
  if (!selectComplete) {
    footerHeight.value = "0px";
    return;
  }
  footerHeight.value = "70px";

  const willBeHiddenSelectedRow =
    clickEventOffsetY + 20 >
    window.innerHeight - parseInt(footerHeight.value) - bottomPadding;

  if (willBeHiddenSelectedRow) {
    nextTick(() => {
      contentArea.value!.scrollBy({
        top: parseInt(footerHeight.value) + 20,
        behavior: "smooth",
      });
    });
  }
};

/** 버스 호차 선택 모달 */
const shouldShowSelectBusOrderModal = ref(false);

const toggleShowSelectBusOrderModal = (show: boolean) => {
  shouldShowSelectBusOrderModal.value = show;
};

/** 즐겨찾기 눌렀을때 로직 처리 */
const showLoginRequriedAlertForFavorite = ref(false);
const showAlertToDeleteFavorite = ref(false);
const onClickFavorite = (setFavorite: boolean) => {
  if (setFavorite && !isLoggedIn.value) {
    showLoginRequriedAlertForFavorite.value = true;
    return;
  }
  if (!setFavorite) {
    showAlertToDeleteFavorite.value = true;
    return;
  }
  toggleSetFavorite(setFavorite);
};

const toggleSetFavorite = (setFavorite: boolean) => {
  showAlertToDeleteFavorite.value = false;
  setFavorite
    ? updateFavoriteLine(MessageTypeCode.FAVORITE)
    : removeFavoriteLine();
};

const createNewFavoriteLine = (
  getOnBusStop?: IBusStop,
  getOffBusStop?: IBusStop
) => {
  if (!lineInfo.value) return;

  const turn = lineInfo.value.turns[selectedTimeIndex.value];
  const favoriteLineData: IFavoriteLineData = {
    lineId: lineId,
    turnId: selectedTurn.value?.id ?? "",
    lineNum: lineInfo.value.lineNum,
    plateNum: "",
    lineStartTime: turn.firstStop.arrivalTime ?? "",
    routeName: lineInfo.value.routeName,
    lineTypeCode: lineInfo.value.lineTypeCode,
    lineTypeName: lineInfo.value.lineTypeName,
    busStops: [], // 즐겨찾기 설정에서는 불필요
    getOnBusStop: getOnBusStop,
    getOffBusStop: getOffBusStop, // 하차 정류장
    firstBusStop: selectedTurnBusStops.value?.[0], // 즐겨찾기 설정에서는 고려대상아님
    lastBusStop:
      selectedTurnBusStops.value?.[selectedTurnBusStops.value?.length - 1], // 즐겨찾기 설정에서는 고려대상아님
    getOnNotice: false, // 새롭게 등록하는 즐겨찾기이기 때문에 false
    getOffNotice: false, // 새롭게 등록하는 즐겨찾기이기 때문에 false
    monthlyBookChecked: false,
    isBoarding: false,
    lineSets: [],
    lineFare: {} as ILineFare,
    earlyDriveInfos: turn.earlyDriveInfos,
    bookIntervals: turn.bookIntervals,
    buses: turn.buses,
    detourBeginDtm: lineInfo.value.detourBeginDtm,
    detourEndDtm: lineInfo.value.detourEndDtm,
    detourReason: lineInfo.value.detourReason,
  };

  return new FavoriteLine(favoriteLineData);
};

// 메시지 생성 함수
const createToastMessage = (
  messageType: MessageTypeCodeType,
  isSuccess: boolean,
  isDeleted: boolean
) => {
  const resultType = isSuccess ? "success" : "failure";
  const actionType = isDeleted ? "delete" : "add";

  return ACTION_MESSAGES[messageType][resultType][actionType];
};

// 즐겨찾기 변경 (노선 추가, 정류장 추가 및 삭제)
const updateFavoriteLine = async (
  messageType: MessageTypeCodeType,
  getOnBusStop?: IBusStop,
  getOffBusStop?: IBusStop,
  isDeleted = false
) => {
  const favoriteLine = createNewFavoriteLine(getOnBusStop, getOffBusStop);
  if (!favoriteLine) return;

  const getOnTargetDate =
    shouldShowSegment.value && getOnBusStop
      ? dayjs(getOnBusStop.useBeginDtm).format("YYYY-MM-DD")
      : undefined;
  const getOffTargetDate =
    shouldShowSegment.value && getOffBusStop
      ? dayjs(getOffBusStop.useBeginDtm).format("YYYY-MM-DD")
      : undefined;

  const addResult = await lineStore.addFavoriteLine(
    favoriteLine,
    getOnTargetDate,
    getOffTargetDate
  );

  if (addResult.isSuccess) {
    currentFavoriteLine.value = favoriteLine;
    updateFavoriteGetOnBusStop(favoriteLine.getOnBusStop);
    updateFavoriteGetOffBusStop(favoriteLine.getOffBusStop);
    updateFavoriteBusStopToChange();
  }

  const message = createToastMessage(
    messageType,
    addResult.isSuccess,
    isDeleted
  );
  showToastMessage(message, 2500, 0);

  return addResult.isSuccess;
};

// 즐겨찾기 삭제
const removeFavoriteLine = async () => {
  const removeResult = await lineStore.removeFavoriteLine(
    selectedTurn.value?.id ?? "",
    isBookable.value ? selectedGetOnBusStop.value?.id : undefined,
    isBookable.value ? selectedGetOffBusStop.value?.id : undefined
  );
  if (removeResult.isSuccess) {
    currentFavoriteLine.value = undefined;
    updateFavoriteGetOnBusStop();
    updateFavoriteGetOffBusStop();
  }
  const message = removeResult.isSuccess
    ? "즐겨찾기가 삭제되었습니다"
    : "즐겨찾기 삭제에 실패했습니다";
  showToastMessage(message, 2500, 0);
};

// 일예약 달력 화면 처리
const showLoginRequriedAlertForBook = ref(false);
const shouldShowBookDetail = ref(false);
const toggleShowBookDetail = (show: boolean) => {
  shouldShowBookDetail.value = show;
};

// 일예약 처리
const onClickDailyBook = () => {
  if (!isLoggedIn.value) {
    showLoginRequriedAlertForBook.value = true;
    return;
  }
  toggleShowBookDetail(true);
};

// 월예약 달력 화면 처리
const shouldShowMonthlyBookDetail = ref(false);
const toggleShowMonthlyBookDetail = (show: boolean) => {
  shouldShowMonthlyBookDetail.value = show;
};

const bookableDatesInfos = ref<IBookableDatesInfo[]>([]);
const showMonthlyBookDetail = (datesInfos: IBookableDatesInfo[]) => {
  shouldShowMonthlySeatAndPayment.value = false;
  bookableDatesInfos.value = datesInfos;
  toggleShowMonthlyBookDetail(true);
};
provide(PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL, showMonthlyBookDetail);

// 월예약 처리
const monthlyBookSeatAndPayment = ref<typeof MonthlyBookSeatAndPayment>();
const shouldShowMonthlySeatAndPayment = ref(false);
const onClickMonthlyBook = () => {
  shouldShowMonthlySeatAndPayment.value = true;
  nextTick(() => {
    monthlyBookSeatAndPayment.value?.proceedMonthlyBook();
  });
};

/** 노선 상세 모달 처리 */
const shouldShowLineInfoDetailExtended = ref(false);
const toggleShowLineInfoDetailExtended = (show: boolean) => {
  shouldShowLineInfoDetailExtended.value = show;
};

const goToLoginPage = () => {
  showLoginRequriedAlertForBook.value = false;
  showLoginRequriedAlertForFavorite.value = false;
  router.push({ name: RouterName.Login });
};

const onClickConfirmBook = () => {
  toggleShowBookDetail(false);
  router.push({ name: RouterName.MainLogin });
};

// const shouldShowMap = ref(false);
// const toggleShowMap = (show: boolean) => {
//   shouldShowMap.value = show;
// };

const shouldShowBusStopLayoutModal = ref(false);
const selectedBusStopForLocation = ref<IBusStopData>();

const handleOnClickBusStop = (busStop: IBusStopData) => {
  selectedBusStopForLocation.value = busStop;
  if (busStop.shouldShowBusStopLayout) {
    shouldShowBusStopLayoutModal.value = true;
  } else {
    // shouldShowMap.value = true;
    router.push({
      name: RouterName.MapOfLineBusStops,
      params: { turnId: selectedTurn.value?.id, busStopId: busStop.id },
      query: {
        targetDateTime: encodeURIComponent(selectedSegmentDateTime.value ?? ""),
      },
    });
  }
};

const showBusStopTimeGuide = ref(false);

const getLineInfoDetail = async () => {
  showLoadingImage();
  const lineInfoResult = await lineStore.getLineInfoDetail(lineId);
  removeLoadingImage();

  if (!lineInfoResult.isSuccess) {
    showErrorModal(
      "노선 상세 오류",
      lineInfoResult.errorMessage,
      lineInfoResult.resultCode,
      lineInfoResult.errorTime,
      router.back
    );
    return;
  }
  lineInfo.value = lineInfoResult.data;
};

// 메인 페이지를 통해 들어온 경우
const turnId = route.query.turnId?.toString() ?? "";
const handleFavoriteLineFromMainPage = () => {
  if (!lineInfo.value) return;

  const index = lineInfo.value.turns.findIndex((turn) => turn.id === turnId);
  selectedTimeIndex.value = index === -1 ? 0 : index; // selectedTurn 은 selectedTimeIndex에 의해 자동 계산됨

  const getOnBusStopId = route.query.getOnBusStopId?.toString() ?? "";
  const getOffBusStopId = route.query.getOffBusStopId?.toString() ?? "";

  selectedGetOnBusStop.value = lineStore.getBusStopById(getOnBusStopId);
  selectedGetOffBusStop.value = lineStore.getBusStopById(getOffBusStopId);

  checkFavoriteLine();
};

// 노선 검색페이지를 통해 들어온 경우
const handleFavoriteLineFromSearchPage = () => {
  const busStops = lineInfo.value?.turns[0]?.busStops;
  if (!busStops?.length) return;
  // 예약 노선인 경우 승/하차 선택시에만 즐겨찾기 여부 판단
  if (isBookable.value) return;

  // 비예약인 경우 (셔틀 포함) 회차 ID로 즐겨찾기 여부 확인
  const favoriteLine = lineStore.getNonBookableFavoriteLine(
    selectedTurn.value?.id ?? ""
  );

  if (favoriteLine) {
    selectedGetOnBusStop.value = favoriteLine.getOnBusStop;
    selectedGetOffBusStop.value = favoriteLine.getOffBusStop;
    currentFavoriteLine.value = favoriteLine;
  } else {
    // 즐겨찾기가 되어 있지 않은 경우 즐겨찾기 추가가 가능하도록 승/하차 정류장 임의 할당
    // selectedGetOnBusStop.value = busStops[0];
    // selectedGetOffBusStop.value = busStops[busStops.length - 1];
    // showFavoriteLineTip();
  }

  // showFooter.value = true;
};

/**  순환노선(셔틀) 노선 시간표 보기 */
const shouldShowTimeTable = ref(false);
const toggleShowTimeTable = (show: boolean) => {
  shouldShowTimeTable.value = show;
};

const targetBusStopForTimeTable = ref<IBusStop>();
const showTimeTable = (busStop: IBusStop) => {
  targetBusStopForTimeTable.value = busStop;
  toggleShowTimeTable(true);
};

/** 선택된 호차(BusOrder) 관리 */
const selectedBus = ref<IBusLocation>();
const updateSelectedBus = (busLocation: IBusLocation) => {
  selectedBus.value = busLocation;
};

const handleChangeSelectedBus = async (allocUid: string) => {
  const target = busLocations.value.find(
    (busLocation) => busLocation.allocUid === allocUid
  );

  if (!target) return;

  updateSelectedBus(target);
  await handleFetchArrivalTimes(selectedArrivalTimeOption.value);
};

const onChangedArrivalTimeOption = async (option: IArrivalTimeOption) => {
  // '과거도착시각'이 선택된 경우, selectedOption을 업데이트 하지 않고, '과거도착시각'모달을 띄운다.
  if (option.type === ArrivalTimeOptionCode.PAST) {
    toggleShowPastArrivalTimeModal(true);
    return;
  }

  await handleFetchArrivalTimes(option);
};

const { arrivalTimes, selectedArrivalTimeOption, fetchArrivalTimes } =
  useArrivalTimesHandler({
    isInsideShuttle,
    busLocations,
    selectedTurn,
    selectedBus,
  });

const handleFetchArrivalTimes = async (option: IArrivalTimeOption) => {
  showLoadingImage();
  const result = await fetchArrivalTimes(option);
  removeLoadingImage();

  if (!result.isSuccess) {
    showErrorModal(
      `${option.name} 오류`,
      result.errorMessage ?? "예상도착시각 조회에 실패했습니다."
    );
  }
};

/** 과거도착시각 조회를 위해 필요한 정보 */
const paramsForPastArrivalTime = computed(() => ({
  lineTurnUid: selectedBus.value?.lineTurnUid ?? selectedTurn.value?.id,
  busOrder:
    selectedBus.value?.busOrder ?? selectedTurn.value?.buses?.[0]?.busOrder,
}));

/** 과거시각 */
const shouldShowPastArrivalTimeModal = ref(false);
const toggleShowPastArrivalTimeModal = (show: boolean) => {
  shouldShowPastArrivalTimeModal.value = show;
};

/** '승하차 정류장' 즐겨찾기 변경 전 주의사항 알림 모달 */
const favoriteBusStopToChange = ref<IBusStop>();
const updateFavoriteBusStopToChange = (busStop?: IBusStop) => {
  favoriteBusStopToChange.value = busStop;
};

const shouldShowChangeFavoriteBusStopAlert = ref(false);
const toggleShowChangeFavoriteBusStopAlert = (show: boolean) => {
  shouldShowChangeFavoriteBusStopAlert.value = show;
};

const handleClickChangeFavoriteBusStopAlertCancel = () => {
  toggleShowChangeFavoriteBusStopAlert(false);
  updateFavoriteBusStopToChange();
};

const handleClickChangeFavoriteBusStopAlertOk = async () => {
  toggleShowChangeFavoriteBusStopAlert(false);

  if (!favoriteBusStopToChange.value) return;
  const { isGetOnStop } = favoriteBusStopToChange.value;

  if (isGetOnStop) {
    await updateFavoriteLine(
      MessageTypeCode.GET_ON_NOTIFICATION,
      favoriteBusStopToChange.value,
      currentFavoriteLine.value?.getOffBusStop
    );
  } else {
    await updateFavoriteLine(
      MessageTypeCode.GET_OFF_NOTIFICATION,
      currentFavoriteLine.value?.getOnBusStop,
      favoriteBusStopToChange.value
    );
  }
};

/** '승하차 정류장' 즐겨찾기 버튼 클릭 */
const shouldShowSelectFavoriteBusStopTypeAddModal = ref(false);
const shouldShowSelectFavoriteBusStopTypeChangeModal = ref(false);

const selectFavoriteBusStopTarget = ref<IBusStop>();
const updateSelectFavoriteBusStopTarget = (busStop?: IBusStop) => {
  selectFavoriteBusStopTarget.value = busStop;
};

const toggleShowSelectFavoriteBusStopTypeAddModal = (show: boolean) => {
  shouldShowSelectFavoriteBusStopTypeAddModal.value = show;
};
const toggleShowSelectFavoriteBusStopTypeChangeModal = (show: boolean) => {
  shouldShowSelectFavoriteBusStopTypeChangeModal.value = show;
};

const handleSelectFavoriteBusStopType = async (isGetOn: boolean) => {
  const busStop = selectFavoriteBusStopTarget.value;
  if (!busStop) return;

  if (isGetOn) {
    await updateFavoriteLine(
      MessageTypeCode.GET_ON_NOTIFICATION,
      busStop,
      currentFavoriteLine.value?.getOffBusStop
    );
  } else {
    await updateFavoriteLine(
      MessageTypeCode.GET_OFF_NOTIFICATION,
      currentFavoriteLine.value?.getOnBusStop,
      busStop
    );
  }

  handleCloseSelectFavoriteBusStopTypeModal();
};

const handleCloseSelectFavoriteBusStopTypeModal = () => {
  toggleShowSelectFavoriteBusStopTypeAddModal(false);
  toggleShowSelectFavoriteBusStopTypeChangeModal(false);
  updateSelectFavoriteBusStopTarget(undefined);
};

const handleGetOnAndOffStop = async (busStop: IBusStop) => {
  const { getOnBusStop, getOffBusStop } = currentFavoriteLine.value ?? {};

  const hasGetOnFavorite = !!getOnBusStop && getOnBusStop.id === busStop.id;
  const hasGetOffFavorite = !!getOffBusStop && getOffBusStop.id === busStop.id;

  // 이미 승차 or 하차 즐겨찾기인 경우, 모달 없이 바로 제거
  if (hasGetOnFavorite) {
    await updateFavoriteLine(
      MessageTypeCode.GET_ON_NOTIFICATION,
      undefined,
      getOffBusStop,
      true
    );
    return;
  }
  if (hasGetOffFavorite) {
    await updateFavoriteLine(
      MessageTypeCode.GET_OFF_NOTIFICATION,
      getOnBusStop,
      undefined,
      true
    );
    return;
  }

  updateSelectFavoriteBusStopTarget(busStop);

  // 즐겨찾기가 모두 없는 경우 추가 모달, 일부만 있는 경우 변경 모달
  if (!getOnBusStop && !getOffBusStop) {
    toggleShowSelectFavoriteBusStopTypeAddModal(true);
  } else {
    toggleShowSelectFavoriteBusStopTypeChangeModal(true);
  }
};

const handleClickFavoriteStatusButton = async (busStop: IBusStop) => {
  const { isGetOffStop, isGetOnStop } = busStop;

  // [승차 && 하차]인 정류장
  if (isGetOffStop && isGetOnStop) {
    await handleGetOnAndOffStop(busStop);
    return;
  }

  // 미정차 정류장
  if (!isGetOffStop && !isGetOnStop) return;

  const favoriteBusStop = isGetOnStop
    ? currentFavoriteLine.value?.getOnBusStop
    : currentFavoriteLine.value?.getOffBusStop;

  const isFavoriteAlready = busStop.id === favoriteBusStop?.id;

  // 이미 다른 승차/하차 정류장이 즐겨찾기된 경우, 즐겨찾기 정류장 변경 모달 표출
  if (favoriteBusStop && busStop.id !== favoriteBusStop.id) {
    updateFavoriteBusStopToChange(busStop);
    toggleShowChangeFavoriteBusStopAlert(true);
    return;
  }

  let params: [
    MessageTypeCodeType,
    IBusStop | undefined,
    IBusStop | undefined,
    boolean
  ];
  if (isFavoriteAlready) {
    // 즐겨찾기 삭제
    params = isGetOnStop
      ? [
          MessageTypeCode.GET_ON_NOTIFICATION,
          undefined,
          currentFavoriteLine.value?.getOffBusStop,
          true,
        ]
      : [
          MessageTypeCode.GET_OFF_NOTIFICATION,
          currentFavoriteLine.value?.getOnBusStop,
          undefined,
          true,
        ];
  } else {
    // 즐겨찾기 추가
    params = isGetOnStop
      ? [
          MessageTypeCode.GET_ON_NOTIFICATION,
          busStop,
          currentFavoriteLine.value?.getOffBusStop,
          false,
        ]
      : [
          MessageTypeCode.GET_OFF_NOTIFICATION,
          currentFavoriteLine.value?.getOnBusStop,
          busStop,
          false,
        ];
  }

  await updateFavoriteLine(...params);
};

const handleBusLocation = async () => {
  if (!lineInfo.value?.lineTypeCode) {
    await setIntervalCallback(updateBusLocation, true);
    return;
  }

  const result = await shouldGetBusLocationAndFindCampus(
    lineInfo.value?.lineTypeCode,
    lineInfo.value?.campusName
  );
  if (!result.shouldGetLocation && !result.nearbyCampusName) {
    shouldShowLocationRefreshButton.value = false;
    showErrorModal(
      "안내",
      `${lineInfo.value?.campusName} 내에서만 실시간 버스 위치를 조회할 수 있습니다.`
    );
    return;
  }

  await setIntervalCallback(updateBusLocation, true);
};

onMounted(async () => {
  await getLineInfoDetail();
  if (turnId.length) {
    // 메인 페이지를 통해서 들어온 경우
    handleFavoriteLineFromMainPage();
  } else {
    handleFavoriteLineFromSearchPage();
  }
  await handleBusLocation();
});

onBeforeUnmount(() => {
  clearIntervalCallback();
});

const __returned__ = { showErrorModal, showLoadingImage, removeLoadingImage, showToastMessage, serviceStore, lineStore, route, segmentedControl, segments, shouldShowSegment, segmentHeightPx, segmentTitles, currentSegmentIndex, onChangedSegmentIndex, selectedSegmentDateTime, isActiveCurrentSegment, selectedTurnPaths, selectedTurnBusStops, nextBusStopChangeDateTime, lineId, lineInfo, footer, selectBusStopRef, shouldGetBusLocationAndFindCampus, shouldShowLocationRefreshButton, shouldShowBusLocations, shouldShowBusArrivalTimeSelect, driveDatesItemsByTurn, availableTimesForModal, availableTimes, selectedTimeIndex, selectedTurn, allBusesMap, createInsideShuttleBusesMap, createSelectedTurnBusesMap, busesForInfoModal, updateBusesForInfoModal, handleClickBusIcon, isInsideShuttle, isBackwardLine, isBookable, dailyBookInterval, monthlyBookInterval, monthlyBookable, currentFavoriteLine, selectedGetOnBusStop, selectedGetOffBusStop, updateFavoriteGetOnBusStop, updateFavoriteGetOffBusStop, resetSelectedBusStopIfNeeded, isFavoriteLine, showFooter, showSelectTimeModal, toggleSelectTimeModal, onTimeSelected, shouldShowBusInfoModal, toggleShowBusInfoModal, checkFavoriteLine, showFavoriteLineTip, busLocations, updateHasAllBusesArrivedLastStop, resetHasAllBusesArrivedLastStop, updateHasAllBusesEndedDriving, resetHasAllBusesEndedDriving, isBusRunning, updateBusRunningStatus, shouldGetBusLocation, isLoggedIn, isLocationCacheExpired, lastBusStopArrivalThresholdMin, updateBusLocationUpdatedAt, busLocationUpdateIntervalMillis, setIntervalCallback, clearIntervalCallback, getLastBusStop, getFirstBusStop, showToastIfNoBusLocationsOnPathMap, updateBusLocation, handleRefreshBusLocation, turnBusLastSeatCountList, shouldShowLineTurnPathMap, toggleShowLineTurnPathModal, footerHeight, contentArea, bottomNavbarHeight, onChangedBusStopSelection, shouldShowSelectBusOrderModal, toggleShowSelectBusOrderModal, showLoginRequriedAlertForFavorite, showAlertToDeleteFavorite, onClickFavorite, toggleSetFavorite, createNewFavoriteLine, createToastMessage, updateFavoriteLine, removeFavoriteLine, showLoginRequriedAlertForBook, shouldShowBookDetail, toggleShowBookDetail, onClickDailyBook, shouldShowMonthlyBookDetail, toggleShowMonthlyBookDetail, bookableDatesInfos, showMonthlyBookDetail, monthlyBookSeatAndPayment, shouldShowMonthlySeatAndPayment, onClickMonthlyBook, shouldShowLineInfoDetailExtended, toggleShowLineInfoDetailExtended, goToLoginPage, onClickConfirmBook, shouldShowBusStopLayoutModal, selectedBusStopForLocation, handleOnClickBusStop, showBusStopTimeGuide, getLineInfoDetail, turnId, handleFavoriteLineFromMainPage, handleFavoriteLineFromSearchPage, shouldShowTimeTable, toggleShowTimeTable, targetBusStopForTimeTable, showTimeTable, selectedBus, updateSelectedBus, handleChangeSelectedBus, onChangedArrivalTimeOption, arrivalTimes, selectedArrivalTimeOption, fetchArrivalTimes, handleFetchArrivalTimes, paramsForPastArrivalTime, shouldShowPastArrivalTimeModal, toggleShowPastArrivalTimeModal, favoriteBusStopToChange, updateFavoriteBusStopToChange, shouldShowChangeFavoriteBusStopAlert, toggleShowChangeFavoriteBusStopAlert, handleClickChangeFavoriteBusStopAlertCancel, handleClickChangeFavoriteBusStopAlertOk, shouldShowSelectFavoriteBusStopTypeAddModal, shouldShowSelectFavoriteBusStopTypeChangeModal, selectFavoriteBusStopTarget, updateSelectFavoriteBusStopTarget, toggleShowSelectFavoriteBusStopTypeAddModal, toggleShowSelectFavoriteBusStopTypeChangeModal, handleSelectFavoriteBusStopType, handleCloseSelectFavoriteBusStopTypeModal, handleGetOnAndOffStop, handleClickFavoriteStatusButton, handleBusLocation, NavigationBar, LineInfoDetailHeader, LineInfoDetailContent, LineInfoDetailSelectBusStop, LineInfoDetailFooter, BookDetail, Modal, InfoModal, Page, SelectModal, get arrivalTimeOptions() { return arrivalTimeOptions }, get BookIntervalCode() { return BookIntervalCode }, LineTurnPathMap, BusStopLayoutModal, TimeTableModal, BusInfoModal, MonthlyBookSeatAndPayment, MonthlyBookDetailWithDuplicatedSeat, PastArrivalTimeModal, BusOrderSelectModal, LineInfoDetailExtended, FavoriteBusStopTypeSelectModal, ScrollTopControl, PageModal, SegmentedControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})